import React, { useCallback, useEffect, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import EsgEvaluationWorkspace from './EsgEvaluationWorkspace';
import EsgIntroductionView from './EsgIntroductionView';
import EsgChairAllocation from './EsgChairAllocation';
import EsgChangeLeadModal from './EsgChangeLeadModal';
import EsgDropOutModal from './EsgDropOutModal';
import { COUNCIL_NUMBERS, formatDate, formatDateTime, statusBadgeClass, statusLabel } from './esgEvaluationUi';
import '../Year2/year2SquadCreator.css';

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'none', label: 'Not evaluated' },
  { value: 'draft', label: 'Not started' },
  { value: 'partial', label: 'In progress' },
  { value: 'approved', label: 'Complete' },
];

function actionLabel(council) {
  if (!council.started) return 'Not started';
  switch (council.evaluation_status) {
    case 'partial': return 'Continue evaluation';
    case 'approved': return 'View evaluation';
    default: return 'Evaluate';
  }
}

function InfoItem({ label, children }) {
  return (
    <div>
      <div className="esg-squad-detail__div-1">{label}</div>
      <div className="esg-squad-detail__div-2">{children || '—'}</div>
    </div>
  );
}

// Everything about one squad (level, people, chairs) plus a card per council with its evaluation status.
// Used by both the Evaluation screen and the ESG Squads list.
export default function EsgSquadDetail({ squadId, onBack, onChanged, onError, onSuccess, backLabel = 'Back' }) {
  const [squad, setSquad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [council, setCouncil] = useState('');
  const [status, setStatus] = useState('');
  const [openCouncil, setOpenCouncil] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [dropMember, setDropMember] = useState(null);
  const [resuming, setResuming] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await mentorApi.getEsgEvaluationSquads();
      setSquad(list.find((s) => s.id === squadId) || null);
    } catch (err) {
      onError?.("Couldn't load this squad - try again.");
    } finally {
      setLoading(false);
    }
  }, [squadId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  if (showIntro) {
    return <EsgIntroductionView squadId={squadId} onBack={() => setShowIntro(false)} onError={onError} />;
  }

  if (openCouncil) {
    return (
      <EsgEvaluationWorkspace
        squadId={squadId}
        council={openCouncil}
        onBack={() => { setOpenCouncil(null); load(); }}
        onChanged={() => { load(); onChanged?.(); }}
        onError={onError}
        onSuccess={onSuccess}
      />
    );
  }

  const backButton = (
    <button type="button" className="btn-form-toggle-outlined esg-squad-detail__button-3" onClick={onBack}>
      {`← ${backLabel}`}
    </button>
  );

  if (loading) return <div className="animate-fade-in">{backButton}<div className="placeholder-card"><p>Loading squad…</p></div></div>;
  if (!squad) {
    return (
      <div className="animate-fade-in">
        {backButton}
        <div className="placeholder-card"><p>This squad isn't available to you.</p></div>
      </div>
    );
  }

  // Members who dropped out stay in the table (with their reason) but are not members any more.
  const allMembers = squad.members || [];
  const members = allMembers.filter((m) => m.status !== 'removed');
  const droppedMembers = allMembers.filter((m) => m.status === 'removed');
  const canDropOut = squad.status === 'active';

  const resume = async () => {
    setResuming(true);
    try {
      await mentorApi.resumeEsgSquad(squadId);
      onSuccess?.('The squad can continue.');
      await load();
      onChanged?.();
    } catch (err) {
      onError?.(err?.data?.message || "Couldn't let the squad continue - please try again.");
    } finally {
      setResuming(false);
    }
  };
  const auditors = squad.auditors || [];
  const evaluated = squad.councils.filter((c) => c.evaluation_status === 'approved').length;

  const visible = squad.councils.filter((c) => {
    if (council && c.council_number !== Number(council)) return false;
    if (status === 'none') return !c.evaluation_status;
    if (status) return c.evaluation_status === status;
    return true;
  });

  const leadLock = squad.admin_lead_lock || { locked: false, reason: null };

  const startEvaluation = (c) => {
    if (c.started) setOpenCouncil(c.council_number);
  };

  return (
    <div className="animate-fade-in">
      {backButton}

      <div className="year2-page-header">
        <h2 className="year2-page-title">{squad.name}</h2>
        <p className="year2-page-subtitle">{squad.level?.name || 'ESG Specialist'}</p>
      </div>

      {squad.paused_at && (
        <div className="rejection-banner esg-paused-banner" role="status">
          <span className="esg-paused-banner__text">
            <strong>This squad is paused.</strong>
            {` It has ${members.length} active member${members.length === 1 ? '' : 's'} (3 are needed to complete the level). Council work is on hold until an auditor or a program administrator allows it to continue.`}
            {members.length < 2 && ' With fewer than 2 members it cannot continue.'}
          </span>
          {squad.can_resume && (
            <button type="button" className="esg-btn esg-btn-primary" disabled={resuming} onClick={resume}>
              {resuming ? 'Saving...' : 'Allow the squad to continue'}
            </button>
          )}
        </div>
      )}

      <div className="wizard-card esg-squad-detail__div-4">
        <div className="esg-squad-detail__div-5">
          <InfoItem label="Level">{squad.level?.name}</InfoItem>
          <InfoItem label="Status"><span className="esg-squad-detail__span-6">{squad.status}</span></InfoItem>
          <InfoItem label="Introduction session lead">
            {squad.admin_lead?.name}
            <button
              type="button"
              className="esg-btn esg-btn-secondary esg-squad-detail__button-7"
              disabled={leadLock.locked}
              title={leadLock.locked ? leadLock.reason : undefined}
              onClick={() => setLeadOpen(true)}
            >
              Change
            </button>
            {leadLock.locked && (
              <div className="esg-squad-detail__div-8">{leadLock.reason}</div>
            )}
          </InfoItem>
          <InfoItem label="SPOC">{squad.spoc?.name || squad.spoc_name}</InfoItem>
          <InfoItem label="Auditors">
            {auditors.length === 0 ? '—' : auditors.map((a) => `${a.user?.name || 'Auditor'}${a.is_standby ? ' (standby)' : ''}`).join(', ')}
          </InfoItem>
          <InfoItem label="Created">{formatDate(squad.created_at)}</InfoItem>
        </div>
        <div className="year2-metric-row esg-squad-detail__div-9">
          <div className="year2-metric-chip"><div className="year2-metric-value">{members.length}</div><div className="year2-metric-label">Members</div></div>
          <div className="year2-metric-chip"><div className="year2-metric-value">{`${evaluated}/10`}</div><div className="year2-metric-label">Evaluated</div></div>
        </div>
      </div>

      <div className="students-table-card esg-squad-detail__div-10">
        <table className="students-table">
          <thead>
            <tr><th>Member</th><th>Grade</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={4} className="esg-empty-cell">No members yet.</td></tr>
            )}
            {members.map((m) => (
              <tr key={m.id}>
                <td>
                  <strong>{m.student?.name || 'Student'}</strong>
                  {squad.admin_lead?.id === m.student_id && <span className="checklist-row-badge esg-squad-detail__span-11">Session lead</span>}
                </td>
                <td>{m.student?.grade || '—'}</td>
                <td>Active</td>
                <td className="esg-cell-right">
                  {canDropOut && (
                    <button
                      type="button"
                      className="esg-btn esg-btn-secondary esg-btn-xs"
                      aria-label={`Mark ${m.student?.name || 'member'} as dropped out`}
                      onClick={() => setDropMember(m)}
                    >
                      Drop out
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {droppedMembers.map((m) => (
              <tr key={m.id} className="esg-row-muted">
                <td>{m.student?.name || 'Student'}</td>
                <td>{m.student?.grade || '—'}</td>
                <td colSpan={2}>
                  {`Dropped out${m.dropped_at ? ` on ${formatDate(m.dropped_at)}` : ''}`}
                  {m.dropped_reason ? ` — ${m.dropped_reason}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dropMember && (
        <EsgDropOutModal
          squad={squad}
          member={dropMember}
          onClose={() => setDropMember(null)}
          onChanged={() => { load(); onChanged?.(); }}
          onSuccess={onSuccess}
        />
      )}

      {leadOpen && (
        <EsgChangeLeadModal
          squad={squad}
          onClose={() => setLeadOpen(false)}
          onChanged={() => { load(); onChanged?.(); }}
          onSuccess={onSuccess}
        />
      )}

      <h3 className="esg-squad-detail__h3-12">Chair allocation</h3>
      <EsgChairAllocation
        squadId={squadId}
        missions={squad.chair_missions || []}
        members={members}
        onChanged={load}
        onSuccess={onSuccess}
        onError={onError}
      />

      <div className="esg-squad-detail__div-13">
        <h3 className="esg-squad-detail__h3-14">Councils</h3>
        <select className="form-control-select esg-squad-detail__select-15" aria-label="Filter by council" value={council} onChange={(e) => setCouncil(e.target.value)}>
          <option value="">All councils</option>
          {COUNCIL_NUMBERS.map((n) => <option key={n} value={n}>{`Council ${n}`}</option>)}
        </select>
        <select className="form-control-select esg-squad-detail__select-15" aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {visible.length === 0 && (council || status) ? (
        <div className="placeholder-card"><p>No councils match these filters.</p></div>
      ) : (
        <div className="year2-cohort-grid">
          {!council && !status && (
            <div
              className="year2-cohort-card"
              role="button"
              tabIndex={0}
              aria-label="Introduction Council"
              onClick={() => setShowIntro(true)}
              onKeyDown={(e) => { if (e.key === 'Enter') setShowIntro(true); }}
            >
              <div className="year2-card-header">
                <div>
                  <h3 className="year2-card-title">Introduction Council</h3>
                  <p className="esg-squad-detail__p-16">Squad set-up session</p>
                </div>
                <span className="status-badge status-draft">View only</span>
              </div>
              <p className="esg-squad-detail__p-17">
                Date, minutes, attendance, introductions, ground rules and chairs. Nothing here is evaluated or editable.
              </p>
              <button type="button" className="esg-btn esg-btn-secondary esg-squad-detail__button-18" onClick={(e) => { e.stopPropagation(); setShowIntro(true); }}>
                View
              </button>
            </div>
          )}
          {visible.map((c) => (
            <div
              key={c.council_number}
              className="year2-cohort-card"
              role="button"
              tabIndex={0}
              aria-label={`Council ${c.council_number}`}
              style={c.started ? undefined : { opacity: 0.7, cursor: 'default' }}
              onClick={() => c.started && startEvaluation(c)}
              onKeyDown={(e) => { if (e.key === 'Enter' && c.started) startEvaluation(c); }}
            >
              <div className="year2-card-header">
                <div>
                  <h3 className="year2-card-title">{`Council ${c.council_number}`}</h3>
                  <p className="esg-squad-detail__p-16">
                    {c.scheduled_at ? formatDateTime(c.scheduled_at) : 'Not scheduled yet'}
                  </p>
                  {c.chair?.name && (
                    <p className="esg-card-meta">
                      {`Chair: ${c.chair.name}`}{c.chair.stand_in ? ' (stand-in)' : ''}
                    </p>
                  )}
                  {c.meeting_url && c.meeting_url.startsWith('https://') && (
                    <p className="esg-squad-detail__p-19">
                      🔗{' '}
                      <a href={c.meeting_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Join session</a>
                    </p>
                  )}
                </div>
                <span className={`status-badge status-${statusBadgeClass(c.evaluation_status)}`}>
                  {c.evaluation_status ? statusLabel(c.evaluation_status) : c.started ? 'Not evaluated' : 'Not started'}
                </span>
              </div>

              <div className="year2-metric-row esg-squad-detail__div-20">
                <div className="year2-metric-chip">
                  <div className="year2-metric-value">{c.session_rating ?? '—'}</div>
                  <div className="year2-metric-label">Session rating</div>
                </div>
                <div className="year2-metric-chip">
                  <div className="year2-metric-value">{c.chair_rating ?? '—'}</div>
                  <div className="year2-metric-label">Chair rating</div>
                </div>
                <div className="year2-metric-chip">
                  <div className="year2-metric-value">
                    {c.days_since_previous === null || c.days_since_previous === undefined
                      ? '—'
                      : `${c.days_since_previous} ${c.days_since_previous === 1 ? 'day' : 'days'}`}
                  </div>
                  <div className="year2-metric-label">Since previous council</div>
                </div>
              </div>

              {c.is_revision && <span className="role-badge revised esg-squad-detail__span-21">Revised</span>}

              <button
                type="button"
                className={c.started ? 'esg-btn esg-btn-primary' : 'esg-btn esg-btn-secondary'}
                style={{ width: '100%' }}
                disabled={!c.started}
                onClick={(e) => { e.stopPropagation(); startEvaluation(c); }}
              >
                {actionLabel(c)}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
